import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BRAND } from "../data/site";

const LINKS = [
  { id: "services", label: "Services" },
  { id: "pricing", label: "Pricing" },
  { id: "locations", label: "Locations" },
  { id: "book", label: "Book" },
  { id: "contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setOpen(false);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 md:px-10 lg:px-16 py-5">
        <button
          data-testid="brand-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-2 font-display font-black uppercase tracking-tight text-xl"
        >
          <span className="inline-block w-2.5 h-2.5 bg-[#FF3B30]" />
          <span>{BRAND.short}</span>
          <span className="text-white/40 font-normal text-xs tracking-[0.25em] hidden sm:inline">
            MEDIA AGENCY
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-${l.id}`}
              onClick={() => goTo(l.id)}
              className="text-sm uppercase tracking-[0.2em] text-white/70 hover:text-white transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-cta"
            onClick={() => goTo("book")}
            className="btn-primary text-xs"
          >
            Book a call
          </button>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden w-10 h-10 border border-white/10 flex items-center justify-center"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-black border-t border-white/10"
          >
            <div className="flex flex-col px-6 py-6 gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => goTo(l.id)}
                  className="text-left text-base uppercase tracking-[0.2em] text-white/80 hover:text-white border-b border-white/5 py-3"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="mobile-nav-cta"
                onClick={() => goTo("book")}
                className="btn-primary mt-2"
              >
                Book a call
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
